import { Box, Group } from "@mantine/core";
import { useEffect, useState } from "react";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import {
  getInputBackgroundColor,
  getInputBorderColor,
  getInputErrorBackgroundColor,
  getInputErrorShadow,
  inputErrorBorderColor,
  inputFocusBorderColor,
  inputRadius,
} from "#app/theme/v2/inputStates";
import { spacing } from "#app/theme/v2/spacing";

import { Text, TEXT_LETTER_SPACING, TEXT_LINE_HEIGHTS } from "../Typographies/Text";
import classes from "./InputDimensions.module.css";

type DimensionKey = "thickness" | "diameter" | "height" | "width" | "length";

const DIMENSION_LABELS: Record<DimensionKey, string> = {
  thickness: "Ep",
  diameter: "Ø",
  height: "H",
  width: "Ɩ",
  length: "L",
};

const DIMENSION_KEYS = Object.keys(DIMENSION_LABELS) as DimensionKey[];

const EMPTY_PLACEHOLDER = "--";

type DimensionFieldProps = {
  dimensionKey: DimensionKey;
  value: number | undefined;
  onChange?: (value: number | undefined) => void;
  disabled?: boolean;
  clearable?: boolean;
  autoFocus?: boolean;
};

const DimensionField = ({
  dimensionKey,
  value,
  onChange,
  disabled,
  clearable,
  autoFocus,
}: DimensionFieldProps) => {
  const [inputValue, setInputValue] = useState(value === undefined ? "" : String(value));

  useEffect(() => {
    setInputValue(value === undefined ? "" : String(value));
  }, [value]);

  if (!onChange) {
    return (
      <Text size="small" component="span">
        {value === undefined ? EMPTY_PLACEHOLDER : value}
      </Text>
    );
  }

  const handleBlur = () => {
    if (inputValue === "") {
      if (clearable) {
        onChange(undefined);
      } else {
        setInputValue(value === undefined ? "" : String(value));
      }
      return;
    }

    const parsed = Number(inputValue);
    if (!Number.isNaN(parsed) && parsed !== value) {
      onChange(parsed);
    } else {
      setInputValue(value === undefined ? "" : String(value));
    }
  };

  const fieldWidth = Math.max(inputValue.length, EMPTY_PLACEHOLDER.length, 1);

  return (
    <input
      className={classes.field}
      type="number"
      value={inputValue}
      placeholder={EMPTY_PLACEHOLDER}
      onChange={(e) => setInputValue(e.currentTarget.value)}
      onBlur={handleBlur}
      disabled={disabled}
      aria-label={dimensionKey}
      autoFocus={autoFocus}
      data-autofocus={autoFocus || undefined}
      style={
        {
          "--field-fz": fontSizes[14],
          "--field-fw": fontWeights.regular,
          "--field-lh": TEXT_LINE_HEIGHTS.small,
          "--field-ls": TEXT_LETTER_SPACING.regular.small,
          "--field-color": colors.text.primaryDefault,
          "--field-disabled-placeholder-color": colors.text.secondaryDisabled,
          "--field-width": `${fieldWidth}ch`,
        } as React.CSSProperties
      }
    />
  );
};

type DimensionChangeHandlers = {
  onThicknessChange?: (value: number | undefined) => void;
  onDiameterChange?: (value: number | undefined) => void;
  onHeightChange?: (value: number | undefined) => void;
  onWidthChange?: (value: number | undefined) => void;
  onLengthChange?: (value: number | undefined) => void;
};

type InputDimensionsProps = {
  dimensions?: DimensionKey[];
  thickness?: number;
  diameter?: number;
  height?: number;
  width?: number;
  length?: number;
  unit?: string;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  autoFocus?: boolean;
} & DimensionChangeHandlers;

const InputDimensions = ({
  dimensions,
  thickness,
  diameter,
  height,
  width,
  length,
  onThicknessChange,
  onDiameterChange,
  onHeightChange,
  onWidthChange,
  onLengthChange,
  unit = "mm",
  disabled,
  error,
  clearable,
  autoFocus,
}: InputDimensionsProps) => {
  const firstDimensionKey = dimensions?.[0] ?? DIMENSION_KEYS[0];
  const values: Record<DimensionKey, number | undefined> = {
    thickness,
    diameter,
    height,
    width,
    length,
  };
  const onChangeHandlers: Record<DimensionKey, ((value: number | undefined) => void) | undefined> =
    {
      thickness: onThicknessChange,
      diameter: onDiameterChange,
      height: onHeightChange,
      width: onWidthChange,
      length: onLengthChange,
    };
  const displayedDimensions =
    dimensions ?? DIMENSION_KEYS.filter((key) => values[key] !== undefined);
  const hasValue = displayedDimensions.some((key) => values[key] !== undefined);

  return (
    <Box
      p={spacing[2]}
      className={classes.container}
      data-error={error || undefined}
      data-testid="input-dimensions"
      style={
        {
          display: "flex",
          alignItems: "center",
          borderRadius: inputRadius,
          "--container-bg": getInputBackgroundColor({ disabled, hasValue }),
          "--container-bd": getInputBorderColor({ disabled, hasValue }),
          "--container-focus-bd": inputFocusBorderColor,
          "--container-error-bd": inputErrorBorderColor,
          "--container-error-bg": getInputErrorBackgroundColor({ hasValue }),
          "--container-error-shadow": getInputErrorShadow({ hasValue }),
        } as React.CSSProperties
      }
    >
      <Group gap={spacing[2]} wrap="nowrap" align="center" px={spacing[1]} style={{ flex: 1 }}>
        {displayedDimensions.map((key) => {
          const fieldHasValue = values[key] !== undefined;
          const labelColor =
            disabled && !fieldHasValue
              ? colors.text.secondaryDisabled
              : colors.text.secondaryDefault;

          return (
            <Group key={key} gap={spacing[0.5]} wrap="nowrap" align="center">
              <Text size="small" component="span" color={labelColor}>
                {DIMENSION_LABELS[key]}
              </Text>
              <DimensionField
                dimensionKey={key}
                autoFocus={autoFocus && key === firstDimensionKey}
                value={values[key]}
                onChange={onChangeHandlers[key]}
                disabled={disabled}
                clearable={clearable}
              />
            </Group>
          );
        })}
      </Group>
      <Box
        px={spacing[1]}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          size="small"
          textAlign="right"
          color={disabled && !hasValue ? colors.text.primaryDisabled : colors.text.primaryDefault}
        >
          {unit}
        </Text>
      </Box>
    </Box>
  );
};

export { InputDimensions };
export type { DimensionKey };
